import { useScrollReveal } from '@/hooks/useScrollReveal';

const specCards = [
  {
    category: 'Tiger Prawn',
    spec: 'Peeled & Deveined',
    grade: '30–40 count/kg',
    process: 'IQF',
    pack: '1 kg vacuum pack',
    badge: 'HACCP-Certified',
    badgeColor: 'bg-secondary-100 text-secondary-700',
    image: 'https://readdy.ai/api/search-image?query=Premium%20frozen%20tiger%20prawns%20individually%20quick%20frozen%20on%20white%20tray%2C%20clean%20professional%20product%20photography%20with%20cool%20blue-white%20tones%2C%20high%20detail%20macro%20shot%20showing%20texture%2C%20simple%20studio%20background%2C%20editorial%20food%20product%20styling&width=400&height=300&seq=freshhlock-sku-prawns&orientation=landscape',
  },
  {
    category: 'Basa Fillet',
    spec: 'Skinless & Boneless',
    grade: '200–250 g/fillet',
    process: 'IQF Interleaved',
    pack: '5 kg bulk pack',
    badge: 'Export-Grade',
    badgeColor: 'bg-accent-100 text-accent-700',
    image: 'https://readdy.ai/api/search-image?query=Professional%20product%20shot%20of%20frozen%20white%20fish%20fillets%20neatly%20stacked%20in%20vacuum%20packaging%2C%20clean%20studio%20lighting%2C%20cool%20white%20background%2C%20high-end%20food%20product%20photography%2C%20crisp%20detail%20on%20texture&width=400&height=300&seq=freshhlock-sku-basa&orientation=landscape',
  },
  {
    category: 'Green Peas',
    spec: 'Grade A Sorted',
    grade: '8–9 mm diameter',
    process: 'IQF',
    pack: '10 kg bulk bag',
    badge: 'Verified',
    badgeColor: 'bg-primary-100 text-primary-700',
    image: 'https://readdy.ai/api/search-image?query=Industrial%20frozen%20green%20peas%20in%20bulk%20packaging%20on%20white%20background%2C%20clean%20product%20photography%2C%20bright%20natural%20green%20color%2C%20professional%20food%20product%20shot%2C%20studio%20lighting%2C%20high%20detail&width=400&height=300&seq=freshhlock-sku-peas&orientation=landscape',
  },
];

export default function SKUTrustSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-4">
            The SKU Trust System
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Order With Confidence From a Warehouse You&apos;ve Never Visited
          </h2>
          <p className="text-foreground-500 max-w-3xl mx-auto leading-relaxed">
            Every product on Freshhlock Wholesale is listed against a published specification sheet — so you know exactly what you&apos;re buying: cut, grade, count, pack size, freezing method, and certification. No surprises, no quality drift.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {specCards.map((card) => (
            <div
              key={card.category}
              className="group bg-card-50 rounded-xl overflow-hidden border border-card-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer card-animate"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={card.image}
                  alt={card.category}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-lg font-bold text-primary-700">{card.category}</h3>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${card.badgeColor}`}>
                    {card.badge}
                  </span>
                </div>
                <div className="space-y-1.5 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Specification</span>
                    <span className="text-foreground-700 font-medium">{card.spec}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Grade</span>
                    <span className="text-foreground-700 font-medium">{card.grade}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Process</span>
                    <span className="text-foreground-700 font-medium">{card.process}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Packaging</span>
                    <span className="text-foreground-700 font-medium">{card.pack}</span>
                  </div>
                </div>
                <div className="border-t border-background-200/70 pt-3">
                  <span className="text-xs text-secondary-600 font-semibold flex items-center gap-1.5">
                    <i className="ri-shield-check-line"></i>
                    Published Spec Sheet — Updated Weekly
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 p-6 bg-secondary-50/70 rounded-xl border border-secondary-100/50 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
              <i className="ri-information-line text-lg"></i>
            </div>
            <div>
              <h4 className="font-heading text-base font-bold text-primary-700 mb-1">How Standard SKUs Work</h4>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Suppliers list inventory against a published specification template. Buyers compare like-for-like across suppliers — same cut, same grade, same pack. Freshhlock verifies spec adherence through warehouse audits and batch sampling. If the product doesn&apos;t match the spec sheet, you don&apos;t pay. Simple.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}